import airport from '@mapbox/maki/icons/airport.svg';
import college from '@mapbox/maki/icons/college.svg';
import fireStation from '@mapbox/maki/icons/fire-station.svg';
import historic from '@mapbox/maki/icons/museum.svg';
import hospital from '@mapbox/maki/icons/hospital.svg';
import police from '@mapbox/maki/icons/police.svg';
import prison from '@mapbox/maki/icons/prison.svg';
import shelter from '@mapbox/maki/icons/shelter.svg';
import bridge from '../assets/mapIcons/bridge.svg';
import dam from '../assets/mapIcons/dam.svg';
import rainGauge from '../assets/mapIcons/rain-gauge.svg';
import streamGauge from '../assets/mapIcons/stream-gauge.svg';
import wastewater from '../assets/mapIcons/wastewater.svg';

// Icons referenced by LAYER_CONFIGS[layerId].icon
// Maki icons (https://labs.mapbox.com/maki-icons/) plus custom icons in assets/mapIcons
export const MAP_ICONS = {
  airport,
  college,
  'fire-station': fireStation,
  historic,
  hospital,
  police,
  prison,
  shelter,
  bridge,
  dam,
  'rain-gauge': rainGauge,
  'stream-gauge': streamGauge,
  wastewater,
};

export const MAP_ICON_PREFIX = 'map-icon-';
const ICON_SIZE = 15;
const PIXEL_RATIO = 4;
// Supersampling factor used when rasterizing the source SVGs
const RENDER_SCALE = 16;
// Mapbox's SDF encoding: the edge sits at alpha 0.75 and one CSS px spans 1/8 of the range
const SDF_RADIUS = 8 * PIXEL_RATIO;
const SDF_CUTOFF = 0.25;
// Transparent padding so `icon-halo-width` isn't clipped at the image edge
const SDF_BUFFER = 3 * PIXEL_RATIO;
const INF = 1e20;
const maskCache = {};

// Rasterize an icon into a high-res alpha mask: dark pixels become the glyph, light pixels
// (e.g. the white backdrop and highlights in the custom icons) become transparent
const rasterizeMask = img => {
  const width = Math.round((img.naturalWidth || ICON_SIZE) * RENDER_SCALE);
  const height = Math.round((img.naturalHeight || ICON_SIZE) * RENDER_SCALE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const imageData = ctx.getImageData(0, 0, width, height);
  const px = imageData.data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let i = 0; i < px.length; i += 4) {
    const luminance = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
    px[i + 3] = Math.round(px[i + 3] * (1 - luminance));
    px[i] = px[i + 1] = px[i + 2] = 0;
    if (px[i + 3] > 8) {
      const x = (i / 4) % width;
      const y = Math.floor(i / 4 / width);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  ctx.putImageData(imageData, 0, 0);

  const bounds =
    maxX < 0
      ? { x: 0, y: 0, w: width, h: height }
      : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  return { canvas, bounds };
};

// Trim to the glyph's bounding box and fit it into the 15px grid, so the custom 21px icons
// (whose glyphs sit inside a backdrop margin) match the Maki icons in size
const fitMask = ({ canvas: source, bounds }) => {
  const size = ICON_SIZE * PIXEL_RATIO;
  const scale = size / Math.max(bounds.w, bounds.h);
  const w = bounds.w * scale;
  const h = bounds.h * scale;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    source,
    bounds.x,
    bounds.y,
    bounds.w,
    bounds.h,
    (size - w) / 2,
    (size - h) / 2,
    w,
    h,
  );
  return canvas;
};

// 1D squared distance transform (Felzenszwalb & Huttenlocher, as in @mapbox/tiny-sdf)
const edt1d = (grid, offset, stride, length, f, v, z) => {
  v[0] = 0;
  z[0] = -INF;
  z[1] = INF;
  f[0] = grid[offset];

  for (let q = 1, k = 0, s = 0; q < length; q++) {
    f[q] = grid[offset + q * stride];
    const q2 = q * q;
    do {
      const r = v[k];
      s = (f[q] - f[r] + q2 - r * r) / (q - r) / 2;
    } while (s <= z[k] && --k > -1);

    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = INF;
  }

  for (let q = 0, k = 0; q < length; q++) {
    while (z[k + 1] < q) k++;
    const r = v[k];
    const qr = q - r;
    grid[offset + q * stride] = f[r] + qr * qr;
  }
};

const edt = (grid, width, height) => {
  const n = Math.max(width, height);
  const f = new Float64Array(n);
  const v = new Uint16Array(n);
  const z = new Float64Array(n + 1);
  for (let x = 0; x < width; x++) edt1d(grid, x, width, height, f, v, z);
  for (let y = 0; y < height; y++) edt1d(grid, y * width, 1, width, f, v, z);
};

// Convert the alpha mask into a signed distance field Mapbox can tint and halo
const toSdf = mask => {
  const glyph = mask.getContext('2d').getImageData(0, 0, mask.width, mask.height).data;
  const width = mask.width + 2 * SDF_BUFFER;
  const height = mask.height + 2 * SDF_BUFFER;
  const len = width * height;
  const gridOuter = new Float64Array(len).fill(INF);
  const gridInner = new Float64Array(len);

  for (let y = 0; y < mask.height; y++) {
    for (let x = 0; x < mask.width; x++) {
      const a = glyph[4 * (y * mask.width + x) + 3] / 255;
      if (a === 0) continue;

      const j = (y + SDF_BUFFER) * width + x + SDF_BUFFER;
      if (a === 1) {
        gridOuter[j] = 0;
        gridInner[j] = INF;
      } else {
        // Anti-aliased pixels give sub-pixel edge offsets
        const d = 0.5 - a;
        gridOuter[j] = d > 0 ? d * d : 0;
        gridInner[j] = d < 0 ? d * d : 0;
      }
    }
  }

  edt(gridOuter, width, height);
  edt(gridInner, width, height);

  const data = new Uint8ClampedArray(len * 4);
  for (let i = 0; i < len; i++) {
    const d = Math.sqrt(gridOuter[i]) - Math.sqrt(gridInner[i]);
    data[i * 4 + 3] = Math.round(255 - 255 * (d / SDF_RADIUS + SDF_CUTOFF));
  }
  return { width, height, data };
};

// Resolves to the SDF for the map and a plain high-res mask (data URL) for the legend
export const getIconMask = name => {
  if (!maskCache[name]) {
    maskCache[name] = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const mask = fitMask(rasterizeMask(img));
        resolve({ imageData: toSdf(mask), dataUrl: mask.toDataURL() });
      };
      img.onerror = reject;
      img.src = MAP_ICONS[name];
    });
  }
  return maskCache[name];
};

// Register the mask as an SDF image so `icon-color` can tint it
const addMapIconImage = async (map, imageId) => {
  const name = imageId.slice(MAP_ICON_PREFIX.length);
  if (!MAP_ICONS[name]) return;

  const { imageData } = await getIconMask(name);
  if (map.hasImage(imageId)) return;
  map.addImage(imageId, imageData, { sdf: true, pixelRatio: PIXEL_RATIO });
};

// Images are dropped whenever the basemap style changes, so load them on demand
export const registerMapIcons = map => {
  map.on('styleimagemissing', ({ id }) => {
    if (id.startsWith(MAP_ICON_PREFIX)) addMapIconImage(map, id);
  });
};
